/* eslint-disable indent */
import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'src/utils/axios';

import {
  ConcedusProductImport,
  InvestmentOffering,
  IOFeeRate,
  IORevenue,
} from 'src/interfaces/investment-offering';
import { User } from 'src/interfaces/user';
import { Issuer, IssuerFeeRate, IssuerRevenueProjection } from 'src/interfaces/issuer';
import { Status } from 'src/interfaces/common';

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    'Offering',
    'User',
    'Issuer',
    'Liability-Roof-Import',
    'Issuer-Fee-Rate',
    'Offering-Fee-Rate',
    'Issuer-Revenue-Projections',
    'Revenue',
  ],
  endpoints: (builder) => ({
    getUsers: builder.query<User[], string>({
      query: (query) => ({
        url: `/admin/users?${query}`,
      }),
      providesTags: (result) =>
        result
          ? result.map(({ userId }) => ({
              type: 'User',
              id: userId,
            }))
          : [],
    }),

    getIssuers: builder.query<Issuer[], string>({
      query: (query) => ({
        url: `/admin/issuers?${query}`,
      }),
      providesTags: (result) =>
        result
          ? result.map(({ issuerId }) => ({
              type: 'Issuer',
              id: issuerId,
            }))
          : [],
    }),

    getInvestmentOfferings: builder.query<InvestmentOffering[], string>({
      query: (query) => ({
        url: `/admin/investment-offerings?${query}`,
      }),
      providesTags: (result) =>
        result
          ? result.map(({ investmentOfferingId }) => ({
              type: 'Offering',
              id: investmentOfferingId,
            }))
          : [],
    }),

    updateSPVStatus: builder.mutation<
      boolean,
      { status: Status; securitizationVehicleId: number; comment?: string }
    >({
      query: (data) => ({
        url: '/admin/issuer-securitization-vehicle-status',
        method: 'PUT',
        data,
      }),
    }),

    getOfferingLiabilityRoofImport: builder.query<ConcedusProductImport, number>({
      query: (offeringId) => ({
        url: `/admin/investment-offerings/${offeringId}/liability-roof-import`,
      }),
      providesTags: ['Liability-Roof-Import'],
    }),

    saveOfferingLiabilityRoofImport: builder.mutation<
      ConcedusProductImport,
      { offeringId: number }
    >({
      query: ({ offeringId }) => ({
        url: `/admin/investment-offerings/${offeringId}/liability-roof-import`,
        method: 'POST',
      }),
      invalidatesTags: ['Liability-Roof-Import'],
    }),

    getIssuersFeeRates: builder.query<IssuerFeeRate[], string | undefined>({
      query: (query = '') => ({
        url: `/admin/issuers/fee-rates?${query}`,
      }),
      providesTags: (result) =>
        result
          ? result.map(({ issuerId }) => ({
              type: 'Issuer-Fee-Rate',
              id: issuerId,
            }))
          : [],
    }),

    updateIssuerFeeRate: builder.mutation<
      IssuerFeeRate,
      { data: Partial<IssuerFeeRate>; query?: string }
    >({
      query: ({ data }) => ({ url: '/admin/issuers/fee-rates', method: 'PUT', data }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedFeeRate } = await queryFulfilled;
          dispatch(
            adminApi.util.updateQueryData('getIssuersFeeRates', arg.query, (draft) => {
              if (!draft.some((el) => el.issuerId === updatedFeeRate.issuerId)) {
                return [...draft, updatedFeeRate];
              }
              return [...draft].map((el) =>
                el.issuerId === updatedFeeRate.issuerId ? updatedFeeRate : el,
              );
            }),
          );
        } catch (err) {
          console.log('err', err);
        }
      },
    }),

    getInvestmentOfferingsFeeRates: builder.query<IOFeeRate[], undefined>({
      query: () => ({
        url: '/admin/investment-offerings/fee-rates',
      }),
      providesTags: (result) =>
        result
          ? result.map(({ investmentOfferingId }) => ({
              type: 'Offering-Fee-Rate',
              id: investmentOfferingId,
            }))
          : [],
    }),

    getIssueRevenueProjections: builder.query<IssuerRevenueProjection[], undefined>({
      query: () => ({
        url: '/admin/issuers/issuer-revenue-projections',
      }),
      providesTags: (result) =>
        result
          ? result.map(({ issuerRevenueProjectionId }) => ({
              type: 'Issuer-Revenue-Projections',
              id: issuerRevenueProjectionId,
            }))
          : [],
    }),

    saveIssueRevenueProjections: builder.mutation<
      IssuerRevenueProjection,
      Partial<IssuerRevenueProjection>
    >({
      query: (data) => ({
        url: '/admin/issuers/issuer-revenue-projections',
        method: 'POST',
        data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data: revenueProjection } = await queryFulfilled;
          dispatch(
            adminApi.util.updateQueryData('getIssueRevenueProjections', undefined, (draft) => [
              ...draft,
              revenueProjection,
            ]),
          );
        } catch (err) {
          console.log('err', err);
        }
      },
    }),

    updateIssueRevenueProjections: builder.mutation<
      IssuerRevenueProjection,
      Partial<IssuerRevenueProjection>
    >({
      query: (data) => ({
        url: `/admin/issuers/issuer-revenue-projections/${data.issuerRevenueProjectionId}`,
        method: 'PUT',
        data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedRevenueProjection } = await queryFulfilled;
          dispatch(
            adminApi.util.updateQueryData('getIssueRevenueProjections', undefined, (draft) =>
              [...draft].map((el) =>
                el.issuerRevenueProjectionId === updatedRevenueProjection.issuerRevenueProjectionId
                  ? updatedRevenueProjection
                  : el,
              ),
            ),
          );
        } catch (err) {
          console.log('err', err);
        }
      },
    }),

    deleteIssueRevenueProjections: builder.mutation<null, number>({
      query: (issuerRevenueProjectionId) => ({
        url: `/admin/issuers/issuer-revenue-projections/${issuerRevenueProjectionId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            adminApi.util.updateQueryData('getIssueRevenueProjections', undefined, (draft) =>
              [...draft].filter((el) => el.issuerRevenueProjectionId !== arg),
            ),
          );
        } catch (err) {
          console.log('err', err);
        }
      },
    }),

    getInvestmentOfferingsRevenue: builder.query<IORevenue[], null>({
      query: () => ({
        url: '/admin/investment-offerings/revenue',
      }),
      providesTags: (result) =>
        result
          ? result.map(({ investmentOfferingId }) => ({
              type: 'Revenue',
              id: investmentOfferingId,
            }))
          : [],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetIssuersQuery,
  useLazyGetIssuersQuery,
  useGetInvestmentOfferingsQuery,
  useLazyGetInvestmentOfferingsQuery,
  useUpdateSPVStatusMutation,
  useGetOfferingLiabilityRoofImportQuery,
  useSaveOfferingLiabilityRoofImportMutation,
  useGetIssuersFeeRatesQuery,
  useUpdateIssuerFeeRateMutation,
  useGetIssueRevenueProjectionsQuery,
  useSaveIssueRevenueProjectionsMutation,
  useUpdateIssueRevenueProjectionsMutation,
  useDeleteIssueRevenueProjectionsMutation,
  useGetInvestmentOfferingsRevenueQuery,
} = adminApi;
